/**
 * スクリーンショット表示用コンポーネント。
 *
 * - src を渡すと画像を表示
 * - src 未指定 / 画像読み込み失敗時はプレースホルダ枠を表示（ガイド執筆中の標準動作）
 * - caption は画像下にキャプションとして表示
 * - 画像クリックで全画面オーバーレイ表示（ライトボックス）。
 *   オーバーレイ・ESC キー・閉じるボタンで閉じる
 */
import { useEffect, useState } from "react";

interface Props {
  src?: string;
  alt?: string;
  caption?: string;
  width?: number | string;
}

export function ScreenshotBox({ src, alt, caption, width = "100%" }: Props) {
  const [errored, setErrored] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const showPlaceholder = !src || errored;

  // ESC キーでライトボックスを閉じる
  useEffect(() => {
    if (!zoomed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setZoomed(false);
    };
    window.addEventListener("keydown", onKey);
    // ライトボックス表示中は body スクロールを止める
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [zoomed]);

  return (
    <>
      <figure style={{ margin: "20px 0", textAlign: "center" }}>
        {showPlaceholder ? (
          <div
            style={{
              width,
              maxWidth: 760,
              margin: "0 auto",
              aspectRatio: "16 / 9",
              background: "linear-gradient(135deg, #faf9f7 0%, #f0efeb 100%)",
              border: "2px dashed #d0cfc9",
              borderRadius: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#999",
              fontSize: 14,
              fontStyle: "italic",
            }}
          >
            スクリーンショット準備中
            {src && <span style={{ marginLeft: 6, fontFamily: "monospace", fontSize: 11 }}>({src})</span>}
          </div>
        ) : (
          <img
            src={src}
            alt={alt || caption || ""}
            onError={() => setErrored(true)}
            onClick={() => setZoomed(true)}
            style={{
              width,
              maxWidth: 760,
              margin: "0 auto",
              borderRadius: 8,
              border: "1px solid #e8e7e3",
              display: "block",
              cursor: "zoom-in",
            }}
          />
        )}
        {caption && (
          <figcaption style={{ marginTop: 8, fontSize: 12, color: "#6b6b68" }}>
            {caption}
          </figcaption>
        )}
      </figure>

      {/* ライトボックス */}
      {zoomed && src && !errored && (
        <div
          onClick={() => setZoomed(false)}
          role="dialog"
          aria-modal="true"
          aria-label="画像の拡大表示"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.85)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            cursor: "zoom-out",
            overflow: "auto",
          }}
        >
          {/* 閉じるボタン */}
          <button
            onClick={(e) => { e.stopPropagation(); setZoomed(false); }}
            aria-label="閉じる"
            style={{
              position: "fixed",
              top: 16,
              right: 20,
              background: "rgba(255,255,255,0.15)",
              color: "white",
              border: "none",
              borderRadius: "50%",
              width: 40,
              height: 40,
              fontSize: 22,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
            }}
          >×</button>

          <img
            src={src}
            alt={alt || caption || ""}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "100%",
              maxHeight: "calc(100vh - 80px)",
              objectFit: "contain",
              cursor: "default",
              boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
              borderRadius: 8,
            }}
          />

          {caption && (
            <div
              style={{
                position: "fixed",
                bottom: 16,
                left: 0,
                right: 0,
                textAlign: "center",
                color: "rgba(255,255,255,0.85)",
                fontSize: 13,
                pointerEvents: "none",
              }}
            >
              {caption}
            </div>
          )}
        </div>
      )}
    </>
  );
}
